import { Injectable } from "@angular/core";
import { v4 as uuid } from "uuid";
import { SortByAttribute } from "@hsgovtech/hsutils";
import { Directory } from "@capacitor/filesystem";
import { EventService } from "./event.service";
import { InspectionRepository } from "../repositories/inspection.repository";
import { SelectedInspectionRepository } from "../repositories/selected.inspection.repository";
import { InspectionViolationRepository } from "../repositories/inspection.violation.repository";
import { IrefItemFvoRepository } from "../repositories/iref.item.fvo.repository";
import { Inspection } from "../types/Inspection/Inspection";
import { InspectionViolation } from "../types/Violation/InspectionViolation";
import { TamConfig } from "../types/Client/TamConfig";
import { ClientService } from "./client.service";
import { LocationService } from "./location.service";
import { LocationListItem } from "../types/Location/LocationListItem";
import { PrintableRepository } from "../repositories/printable.repository";
import { PrintableLookUpRepository } from "../repositories/printable.lookup.repository";
import { LogService } from "./log.service";
import { BaseService } from "./base.service";
import { CategoryService } from "./category.service";
import { ObservationService } from "./observation.service";
import { handlePromise } from "../../utils";
import { InspectionTypeService } from "./inspection.type.service";
import { ViolationCategory } from "../types/Violation/ViolationCategory";
import { Location } from "../types/Location/Location";
import { ViolationObservation } from "../types/Violation/ViolationObservation";
import { InspectionType } from "../types/Inspection/InspectionType";
import { ViolationItem } from "../types/Violation/ViolationItem";
import { DateService } from "./date.service";
import { Inspections } from "../types/Inspection/Inspections";
import { PerformanceMetricsService } from "./performance.metrics.service";

@Injectable({
  providedIn: "root",
})
export class InspectionService extends BaseService {
  constructor(
    private inspectionTypeService: InspectionTypeService,
    private clientService: ClientService,
    private eventService: EventService,
    private inspectionRepo: InspectionRepository,
    private inspectionViolationRepo: InspectionViolationRepository,
    private categoryService: CategoryService,
    private observationService: ObservationService,
    private irefItemFvoRepo: IrefItemFvoRepository,
    private selectedInspectionRepo: SelectedInspectionRepository,
    private locationService: LocationService,
    private printableRepo: PrintableRepository,
    private printableLookUpRepo: PrintableLookUpRepository,
    private dateService: DateService,
    private performanceMetrics: PerformanceMetricsService,
    logService: LogService,
  ) {
    super(logService, "InspectionService");
  }

  public getById(inspectionID: string): Inspection | undefined {
    return this.inspectionRepo.getInspectionByInspectionID(inspectionID);
  }

  public setPreviousReloadDataCopied(didCopy: boolean) {
    const inspection = this.getSelectedInspection();
    return this.save({ ...inspection, previousReloadDataCopied: didCopy });
  }

  public getPrintable(inspectionTypeID: string): any {
    const printables =
      this.printableRepo.getPrintablesByInspectionTypeID(inspectionTypeID);
    const fileNames = printables.map((printable) => {
      const lookup = this.printableLookUpRepo.getById(printable.printableID);
      return lookup.pageName;
    });

    const filePaths = fileNames.map((x) => `/${Directory.Data}/${x}`);
    const client = this.clientService.getCurrentClient();
    const printableRecords: any = client?.clientData?.images || [];
    const images = printableRecords
      .filter((x: any) => filePaths.includes(x.path))
      .map((photo: any) => photo.path);
    return images;
  }

  public searchInspections(
    keys: string[],
    values: any[],
  ): { [key: string]: Inspection } {
    return this.inspectionRepo.searchInspections(keys, values);
  }

  public saveInspection(inspection: Inspection): Promise<{
    [key: string]: Inspection;
  }> {
    return this.inspectionRepo.saveInspection(inspection);
  }

  public saveInspections(inspections: Inspections): Promise<{
    [key: string]: Inspection;
  }> {
    const selectedInspection = this.getSelectedInspection();
    return this.inspectionRepo.saveChangesToClient(
      inspections,
      selectedInspection,
    );
  }

  private mergeEdits(oldEdits: any, newEdits: any) {
    const combined = { ...oldEdits, ...newEdits };
    const mergedEdits = Object.entries(combined).reduce(
      (acc: any, [key, value]: any) => {
        if (newEdits[key]) {
          return { ...acc, [key]: value };
        }
        return { ...acc };
      },
      {},
    );
    return mergedEdits;
  }

  public getCategories(
    inspection: Inspection,
    history: any,
  ): ViolationCategory[] {
    const followUpConfigs = this.inspectionTypeService.getFollowUpConfigs(
      inspection.inspectionTypeID,
    );
    return this.categoryService.getCategories(
      inspection,
      followUpConfigs,
      history,
    );
  }

  private prepareInspectionForSave(
    inspection: Inspection,
    locationEdits: any = {},
    finalize: boolean = false,
  ): Inspection {
    return {
      ...inspection,
      locationEdits: this.mergeEdits(inspection.locationEdits, locationEdits),
      finalized: inspection.finalized === "1" || finalize ? "1" : "0",
      dateLastModified: Date.now(),
      inspectionDate: this.dateService.formatDate(
        inspection?.record?.inspectionDate || inspection.inspectionDate,
      ),
    };
  }

  public getLocationEdits(locationRecord: any, locationFormValues: any) {
    const locationEdits = Object.entries(locationFormValues).reduce(
      (acc: any, [key, value]: any) => {
        if (locationRecord[key] !== value) {
          return { ...acc, [key]: value };
        }
        return { ...acc };
      },
      {},
    );
    return locationEdits;
  }

  public async save(
    record: Inspection,
    locationEdits: any = {},
    finalize: boolean = false,
  ) {
    //	If inspection is finalized then update the location with edits
    if (finalize) {
      const location = this.locationService.getLocationByID(record.locationID);
      await this.locationService.saveLocations({
        [location.locationID]: {
          ...location,
          record: { ...location.record, ...locationEdits },
        },
      });
    }
    const recordToSave = this.prepareInspectionForSave(
      record,
      locationEdits,
      finalize,
    );
    await this.inspectionRepo.saveInspection(recordToSave);
    return recordToSave;
  }

  public saveInspectionViolation(
    locationID: string,
    inspectionID: string,
    violation: InspectionViolation,
  ): Promise<InspectionViolation[]> {
    return this.inspectionViolationRepo.saveInspectionViolation(
      locationID,
      inspectionID,
      violation,
    );
  }

  public async removeInspection(inspectionID: string) {
    const [err, res] = await handlePromise(
      this.inspectionRepo.removeInspection(inspectionID),
    );
    if (err) {
      this.logger.error("Unable To Remove Inspection", err);
    }
    return res;
  }

  public getAllInspections(): Inspections {
    return this.inspectionRepo.getAllInspections();
  }

  public getAllFinalizedInspections(): Inspection[] {
    const locations = this.locationService.getLocationsAsArray();
    return locations.flatMap((location: Location) =>
      this.inspectionRepo.getFinalizedInspectionsAsArray(location.locationID),
    );
  }

  public getTamConfigs(): TamConfig {
    const clientSettings = this.clientService.getCurrentClientConfig();
    return clientSettings?.tamConfigs || {};
  }

  public inspectionExists(inspectionID: string) {
    const inspection = this.getById(inspectionID);
    return inspection && Object.keys(inspection).length;
  }

  async setSelectedInspection(inspection: Inspection): Promise<Inspection> {
    if (!this.inspectionExists(inspection.inspectionID)) {
      await this.saveInspection(inspection);
    }
    return this.selectedInspectionRepo.set(inspection);
  }

  public getSelectedInspection(): Inspection {
    return this.selectedInspectionRepo.get();
  }

  public hasSelectedInspection(): boolean {
    return !!this.selectedInspectionRepo.get();
  }

  public clearSelectedInspection() {
    return this.selectedInspectionRepo.clear();
  }

  public getCustomViolationFields(itemID: string) {
    return this.irefItemFvoRepo.getIrefItemFvosByItemID(itemID);
  }

  public getObservationFromMap(itemID: string) {
    return this.observationService.getObservationFromMap(itemID);
  }

  private updateViolationEntryData(
    inspection: Inspection,
    categories: ViolationCategory[],
  ) {
    const followUpConfigs = this.inspectionTypeService.getFollowUpConfigs(
      inspection.inspectionTypeID,
    );
    const sortedCategories = SortByAttribute(categories, "sort");
    const res = this.categoryService.indexViolationEntryData(
      inspection,
      sortedCategories,
      followUpConfigs,
      inspection.history,
    );
    return res;
  }

  private getViolationInfoValue(violationInfo: any): InspectionViolation {
    return violationInfo?.getRawValue?.() || violationInfo?.value || {};
  }

  public async saveViolationItem(
    inspection: Inspection,
    violationInfo: any,
  ): Promise<void> {
    const violationInfoValue = this.getViolationInfoValue(violationInfo);
    const markerID = this.performanceMetrics.start(
      "inspection-violation-save",
      {
        inspectionID: inspection.inspectionID,
        locationID: inspection.locationID,
        violationID: violationInfoValue.violationID,
        observationID: violationInfoValue.observationID,
        itemID: violationInfoValue.itemID,
      },
    );
    try {
      await this.inspectionViolationRepo.saveInspectionViolation(
        inspection.locationID,
        inspection.inspectionID,
        { ...violationInfoValue },
      );
      this.performanceMetrics.step(markerID, "violation-saved", {
        payloadSize: Object.keys(violationInfoValue).length,
      });
      this.observationService.setObservationMap(
        inspection.locationID,
        inspection.inspectionID,
      );
      const updatedInspection = this.inspectionRepo.getInspectionByInspectionID(
        inspection.inspectionID,
      );
      if (updatedInspection) {
        this.eventService.emit("app.inspections.violationSaved", {
          ...violationInfoValue,
        });
        this.performanceMetrics.stop(markerID, "success", {
          violationCount: updatedInspection?.violations?.length || 0,
        });
      } else {
        this.performanceMetrics.stop(markerID, "error", {
          reason: "inspection-not-found",
        });
        this.logger.error({
          functionName: "saveViolationItem",
          message: "Inspection could not be found by id",
          violationInfo,
          inspection,
        });
      }
    } catch (err: any) {
      this.performanceMetrics.stop(markerID, "error", {
        errorMessage: err?.message,
      });
      throw err;
    }
  }

  public async removeViolationItem(
    item: any,
    violation: any,
    inspection: Inspection,
    categories: ViolationCategory[],
  ): Promise<any> {
    const markerID = this.performanceMetrics.start(
      "inspection-violation-remove",
      {
        inspectionID: inspection.inspectionID,
        locationID: inspection.locationID,
        violationID: violation?.violationID,
        itemID: item?.itemID,
      },
    );
    try {
      await this.inspectionViolationRepo.removeInspectionViolations(
        inspection.locationID,
        inspection.inspectionID,
        [violation.violationID],
      );
      this.performanceMetrics.step(markerID, "violation-removed-from-repo");
      this.observationService.setObservationMap(
        inspection.locationID,
        inspection.inspectionID,
      );
      const res = this.updateViolationEntryData(inspection, categories);
      const updatedInspection = this.inspectionRepo.getInspectionByInspectionID(
        inspection.inspectionID,
      );
      if (updatedInspection) {
        this.eventService.emit("app.inspections.violationRemoved", violation);
        this.performanceMetrics.stop(markerID, "success", {
          violationCount: updatedInspection?.violations?.length || 0,
        });
      } else {
        this.performanceMetrics.stop(markerID, "error", {
          reason: "inspection-not-found",
        });
        this.logger.error({
          functionName: "removeViolationItem",
          message: "Inspection could not be found by id",
          item,
          violation,
          inspection,
        });
      }
      return res;
    } catch (err: any) {
      this.performanceMetrics.stop(markerID, "error", {
        errorMessage: err?.message,
      });
      throw err;
    }
  }

  async quickSaveViolation(
    item: ViolationItem,
    observation: ViolationObservation,
    violationFields: any,
    inspection: Inspection,
    categories: ViolationCategory[],
  ) {
    this.eventService.emit(
      "script.inspection.itemTapped.in",
      item,
      null,
      observation,
    );
    const fields = violationFields.reduce((acc: any, ff: any) => {
      return { ...acc, [ff.properties.id]: "" };
    }, {});

    const fieldData = {
      ...fields,
      violationID: uuid(),
      itemID: item.itemID,
      observationID: observation.observationID,
      inspectionID: inspection.inspectionID,
    };
    await this.saveViolationItem(inspection, { value: fieldData });
    const res = this.updateViolationEntryData(inspection, categories);
    this.eventService.emit("app.inspections.violationSaved", fieldData);
    return res;
  }

  public createFollowUpInspection(
    listItem: LocationListItem,
    followUpID: string,
  ) {
    const locationHistoryItem: any = listItem.history[followUpID];
    const inspType: InspectionType = this.inspectionTypeService.getById(
      locationHistoryItem.type,
    );
    const currentClient = this.clientService.getCurrentClient();
    const location = this.locationService.getLocationByID(listItem.locationID);
    const inspectionID = uuid();
    const inspectionTypeID = inspType.inspectionTypeID || "";
    const inspectionRecord: Inspection = {
      previousReloadDataCopied: false,
      inspectionID,
      history: listItem.history,
      tabs: {},
      violations: [],
      photos: [],
      locationEdits: {},
      dateLastModified: Date.now(),
      locationID: listItem.locationID,
      inspectionTypeID,
      followUpID,
      finalized: "0",
      synced: "0",
      inspectorID: currentClient.userID,
      record: {
        inspectionID,
        userID: currentClient.userID,
        parentFormID: location.parentFormID,
        inspectionTypeID,
      },
      isNew: true,
    };

    const followUpConfigs = this.inspectionTypeService.getFollowUpConfigs(
      inspType.inspectionTypeID,
    );

    const history: any =
      this.observationService.getObservationsFromAllPreviousInspections(
        listItem.history,
        followUpConfigs.copyOnlyViolations,
      );

    const violations = Object.values(history).flatMap((item: any) =>
      item
        .map((violation: any) => {
          if (violation.inspectionID === followUpID) {
            let newViolation: any = {
              violationID: uuid(),
              inspectionID: inspectionRecord.inspectionID,
              inspectionId: inspectionRecord.inspectionID,
              observationID: violation.observationID,
              itemID: violation.itemID,
            };
            const observation =
              this.observationService.getIrefItemObservationByObservationID(
                violation.observationID,
              );
            let saveViolation = true;
            let saveViolationDetails = true;

            if (
              followUpConfigs.copyOnlyViolations &&
              observation.isViolation === 0
            ) {
              saveViolation = false;
            }

            if (!followUpConfigs.prefillObservations) {
              saveViolationDetails = false;
            }

            if (saveViolationDetails) {
              newViolation = { ...violation, ...newViolation };
            }

            if (saveViolation) {
              const { incrementalIdentifier, ...violationToSave } =
                newViolation;
              return violationToSave;
            }
          }
          return false;
        })
        .filter((v: any) => v),
    );
    return { ...inspectionRecord, violations } as Inspection;
  }

  public sendBackToQueue(inspectionID: string) {
    const inspection = this.getById(inspectionID);
    if (inspection) {
      return this.saveInspection({ ...inspection, synced: "0" });
    }
    return this.logger.error({
      functionName: "sendBackToQueue",
      message: "Inspection could not be found by id",
      inspectionID,
    });
  }
}
